import React, { ReactNode } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData, PolicyTypes, ReschedulePolicyData } from '../../api';
import { customerFacingPriceFormatter } from '../../orderPage/utils';
import { phoneMediaQuery, rescheduleTheme } from '../rescheduleTheme';
import { RescheduleTypography } from './RescheduleTypography';

type PolicyStripProps = {
  policy: ReschedulePolicyData;
  currentTierHoursBefore?: number;
  account: AccountData;
};

type PolicyRow = {
  key: string;
  range: string;
  value: ReactNode;
  isCurrent: boolean;
  isClosed: boolean;
};

export const PolicyStrip = ({ policy, currentTierHoursBefore, account }: PolicyStripProps) => {
  const { t } = useTranslation();
  const isPhone = useMediaQuery(phoneMediaQuery);

  const tiers = [...(policy.fee_tiers ?? [])].sort((first, second) => second.hours_before - first.hours_before);
  const minimumHoursBefore = policy.min_hours_before ?? 0;
  if (tiers.length === 0 && minimumHoursBefore === 0) {
    return null;
  }

  const freeAboveHours = tiers.length > 0 ? tiers[0].hours_before : minimumHoursBefore;
  const isFreeCurrent = currentTierHoursBefore == null;

  const rows: PolicyRow[] = [
    {
      key: 'free',
      range: t('manage_booking.v2.freeAbove', { h: freeAboveHours }),
      value: t('manage_booking.v2.free'),
      isCurrent: isFreeCurrent,
      isClosed: false,
    },
    ...tiers.map((tier, index) => ({
      key: `tier${tier.hours_before}`,
      range: t('manage_booking.v2.tierRange', {
        hi: tier.hours_before,
        lo: tiers[index + 1]?.hours_before ?? minimumHoursBefore,
      }),
      value:
        tier.policy_type === PolicyTypes.PERCENT ? `${tier.value}%` : customerFacingPriceFormatter(tier.value, account),
      isCurrent: currentTierHoursBefore === tier.hours_before,
      isClosed: false,
    })),
  ];
  if (minimumHoursBefore > 0) {
    rows.push({
      key: 'closed',
      range: t('manage_booking.v2.closedUnder', { h: minimumHoursBefore }),
      value: t('manage_booking.v2.noChanges'),
      isCurrent: false,
      isClosed: true,
    });
  }

  const nowBadge = (
    <RescheduleTypography
      component="span"
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 7px',
        borderRadius: 10,
        background: '#FFFFFF',
        color: rescheduleTheme.brand,
      }}>
      {t('manage_booking.v2.now')}
    </RescheduleTypography>
  );

  if (isPhone) {
    return (
      <Grid
        component="ul"
        container
        flexDirection="column"
        flexWrap="nowrap"
        sx={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          border: `1px solid ${rescheduleTheme.border}`,
          borderRadius: `${rescheduleTheme.radii.cardMobile}px`,
          overflow: 'hidden',
        }}>
        {rows.map((row, index) => (
          <Grid
            component="li"
            container
            key={row.key}
            aria-current={row.isCurrent ? 'true' : undefined}
            flexWrap="nowrap"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              padding: '10px 14px',
              borderBottom: index < rows.length - 1 ? `1px solid ${rescheduleTheme.divider}` : undefined,
              background: row.isCurrent
                ? rescheduleTheme.brand
                : row.isClosed
                ? rescheduleTheme.surfaceMuted
                : '#FFFFFF',
              color: row.isCurrent ? '#FFFFFF' : row.isClosed ? rescheduleTheme.textMuted : rescheduleTheme.text,
            }}>
            <Grid container flexWrap="nowrap" alignItems="center" width="auto" gap="8px">
              <RescheduleTypography component="span" style={{ fontSize: 14 }}>
                {row.range}
              </RescheduleTypography>
              {row.isCurrent && nowBadge}
            </Grid>
            <RescheduleTypography component="span" dir="ltr" style={{ fontSize: 14, fontWeight: 600 }}>
              {row.value}
            </RescheduleTypography>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid component="ul" container flexWrap="nowrap" sx={{ listStyle: 'none', margin: 0, padding: 0, gap: '6px' }}>
      {rows.map((row) => (
        <Grid
          component="li"
          container
          key={row.key}
          aria-current={row.isCurrent ? 'true' : undefined}
          flexDirection="column"
          flexWrap="nowrap"
          sx={{
            flex: '1 1 0',
            minWidth: 0,
            gap: '4px',
            padding: '12px 10px',
            borderRadius: '8px',
            background: row.isCurrent
              ? rescheduleTheme.brand
              : row.isClosed
              ? `repeating-linear-gradient(135deg, ${rescheduleTheme.surfaceMuted} 0 6px, ${rescheduleTheme.divider} 6px 12px)`
              : rescheduleTheme.surfaceMuted,
            color: row.isCurrent ? '#FFFFFF' : row.isClosed ? rescheduleTheme.textMuted : rescheduleTheme.text,
          }}>
          <Grid container flexWrap="nowrap" alignItems="center" justifyContent="space-between" gap="6px">
            <RescheduleTypography component="span" dir="ltr" style={{ fontSize: 17, fontWeight: 600 }}>
              {row.value}
            </RescheduleTypography>
            {row.isCurrent && nowBadge}
          </Grid>
          <RescheduleTypography style={{ fontSize: 13, opacity: 0.85 }}>{row.range}</RescheduleTypography>
        </Grid>
      ))}
    </Grid>
  );
};
